import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { ReactHookForm } from '../../../../components/hook-form/react-hook-form.component';
import { useReactHookForm } from '../../../../hooks/useReactHookForm';
import { moveContactFormSchema } from '../move-contact-form.schema';
import { contactApiKeyStore } from '../../../../api/contact-api-key-store';
import { FormAutoCompleteSelectDynamic } from '../../../../components/hook-form/form-autocomplete-select.dynamic.component';
import { LoadingIndicator } from '../../../../components/loading-indicator.component';
import { useSaveData } from '../../../../hooks/useSaveData';
import { customersApiKeyStore } from '../../../../api/customers-api-key-store';
import { MoveContactComponent } from './move-contact.component';
import { ContactMapListComponent } from './contact-map-list.component';
import { useLoadData } from '../../../../hooks/useLoadData';
import { ApiResponse } from '../../../../models/api-response';
import { useAuthorize } from '../../../../hooks/use-authorize';
import { UserPermissionName } from '../../../../models/user-permission-name';

interface MoveContact {
  contactData: any;
  contactID: number;
  customerID: number;
  onClosed: () => void;
}

const retriveIdForOldData = (data: any) => {
  const officeIDs: any = [];
  const projectIDs: any = [];
  const unitIDs: any = [];

  Array.isArray(data) &&
    data?.forEach((office: any) => {
      if (office.officeMapping) {
        officeIDs.push(office.officeID);
      }
      Array.isArray(office?.contactsProjectMappings) &&
        office.contactsProjectMappings?.forEach((project: any) => {
          if (project.projectMapping) {
            projectIDs.push(project.projectID);
          }
          Array.isArray(project?.contactsUnitMappings) &&
            project.contactsUnitMappings?.forEach((unit: any) => {
              if (unit.unitMapping) {
                unitIDs.push(unit.unitID);
              }
            });
        });
    });
  return { officeIDs: officeIDs, projectIDs: projectIDs, unitIDs: unitIDs };
};

export const MoveContactDialog = ({ contactData, contactID, customerID, onClosed }: MoveContact) => {
  const form = useReactHookForm({
    defaultValues: {
      contact: { contactsID: contactID ?? 0, contactsFullName: '' },
      customer: { customerID: customerID ?? 0, customerName: '' },
      office: { officeID: '', officeName: '' },
      project: { projectID: '', projectName: '' },
      unit: { unitID: '', unitName: '' },
    },
    yupSchema: moveContactFormSchema,
  });
  const [newMappedData, setNewMappedData] = useState<any[]>([]);
  const [mappedIds, setMappedIds] = useState<any>();
  const [customerChange, setCustomerAction] = useState<boolean>(false);
  const [selectedCustomerID, setCustomerID] = useState<any>(customerID);

  const { data, isLoading, isFetching, refetch } = useLoadData<ApiResponse<any>>(
    contactApiKeyStore.getMappingContacts(contactID),
    !!contactID && contactID > 0
  );

  const { isLoading: isMovingContactLoading, mutateAsync } = useSaveData(contactApiKeyStore.addContactMapping(), () =>
    contactApiKeyStore.getContacts()
  );

  useEffect(() => {
    if (data) {
      setMappedIds(retriveIdForOldData(data?.data?.contactsOfficeMappings ?? []))
      setNewMappedData(data?.data?.contactsOfficeMappings ?? []);
    }
  }, [data])

  const retriveIds = () => {
    const officeIDs: any = [];
    const projectIDs: any = [];
    const unitIDs: any = [];

    Array.isArray(newMappedData) &&
      newMappedData?.forEach((office: any) => {
        if (office.isAdded) {
          officeIDs.push(office.officeID);
        }
        Array.isArray(office?.contactsProjectMappings) &&
          office.contactsProjectMappings?.forEach((project: any) => {
            if (project.isAdded) {
              projectIDs.push(project.projectID);
            }
            Array.isArray(project?.contactsUnitMappings) &&
              project.contactsUnitMappings?.forEach((unit: any) => {
                if (unit.isAdded) {
                  unitIDs.push(unit.unitID);
                }
              });
          });
      });
    return { officeIDs: officeIDs, projectIDs: projectIDs, unitIDs: unitIDs };
  };

  const removeDuplicates = (data1: any, data2: any) => {
    return {
      officeIDs: data1.officeIDs.filter((id: any) => !data2.officeIDs.includes(id)),
      projectIDs: data1.projectIDs.filter((id: any) => !data2.projectIDs.includes(id)),
      unitIDs: data1.unitIDs.filter((id: any) => !data2.unitIDs.includes(id))
    };
  };

  const handleSubmit = async (movingData: any) => {
    const listOfIds = removeDuplicates(retriveIds(), mappedIds)
    const data = {
      contactsID: Number(contactID),
      customerID: Number(movingData?.customer?.customerID),
      officeIDs: listOfIds.officeIDs,
      projectIDs: listOfIds?.projectIDs,
      unitIDs: listOfIds?.unitIDs,
    };
    await mutateAsync({ data: data });
    onClosed();
  };

  const updateMappedData = (input: any) => {
    setNewMappedData((prevData: any[]) => {
      const updatedData = [...prevData];

      // Check if officeID matches
      const officeIndex = updatedData.findIndex((office) => Number(office.officeID) === Number(input.officeID));

      if (officeIndex !== -1) {
        const office = updatedData[officeIndex];
        if (!office.officeMapping) office.isAdded = office.isAdded ? office.isAdded : input.isAdded;
        office.officeMapping = office.officeMapping ? office.officeMapping : input.officeMapping; // Update officeMapping

        input.contactsProjectMappings.forEach((inputProject: any) => {
          // Check if projectID matches
          const projectIndex = office.contactsProjectMappings.findIndex(
            (project: any) => project.projectID === inputProject.projectID
          );

          if (projectIndex !== -1) {
            const project = office.contactsProjectMappings[projectIndex];
            if (!project.projectMapping) project.isAdded = project.isAdded ? project.isAdded : inputProject.isAdded;
            project.projectMapping = project.projectMapping ? project.projectMapping : inputProject.projectMapping; // Update projectMapping

            inputProject.contactsUnitMappings.forEach((inputUnit: any) => {
              // Check if unitID matches
              const unitIndex = project.contactsUnitMappings.findIndex(
                (unit: any) => unit.unitID === inputUnit.unitID
              );

              if (unitIndex !== -1) {
                const unit = project.contactsUnitMappings[unitIndex];
                if (!unit.unitMapping) unit.isAdded = unit.isAdded ? unit.isAdded : inputUnit.isAdded;
                unit.unitMapping = unit.unitMapping ? unit.unitMapping : inputUnit.unitMapping; // Update unitMapping
              } else {
                // No matching unit, add new unit
                project.contactsUnitMappings.push(inputUnit);
              }
            });
          } else {
            // No matching project, add new project
            office.contactsProjectMappings.push(inputProject);
          }
        });
      } else {
        // No matching office, add new office
        updatedData.push(input);
      }

      return updatedData;
    });
  };

  const handleMappedData = (data: any) => {
    updateMappedData(data?.contactsOfficeMappings[0]);
  }

  const onChangeCustomer = (value: any) => {
    setCustomerAction(!!value);
    setCustomerID(value?.customerID);
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <Dialog
      open={true}
      fullWidth={true}
      maxWidth={'xl'}
    // onClose={onClosed}
    >
      <DialogTitle id="scroll-dialog-title">Contact Map / Move</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClosed}
        sx={{
          position: 'absolute',
          right: 10,
          top: 15,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Tooltip title={<Typography variant="body1">Close</Typography>}>
          <CloseIcon />
        </Tooltip>
      </IconButton>
      <ReactHookForm
        formName="MOVE_CONTACT_FORM"
        onSubmit={handleSubmit}
        formContextProps={form}
        isBusy={isMovingContactLoading}
      >
        <DialogContent dividers className="popupDilog">
          <Grid container spacing={2}>
            <Grid item xl={12} lg={12} sm={12} xs={12}>
              <Grid container spacing={2}>
                <Grid item xl={6} lg={6} sm={6} xs={12}>
                  <LoadingIndicator isLoading={isMovingContactLoading} />
                  <FormAutoCompleteSelectDynamic
                    name="contact"
                    label="Contact"
                    disabled
                    dataItems={contactData}
                    optionsValueKey="contactsID"
                    optionsLabelKey="contactsFullName"
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xl={12} lg={12} sm={12} xs={12}>
              <Grid container spacing={2}>
                <Grid item xl={6} lg={6} sm={6} xs={12}>
                  <FormAutoCompleteSelectDynamic
                    name="customer"
                    label="Customer"
                    onChange={onChangeCustomer}
                    disabled={newMappedData?.length > 0}
                    getQueryKeyConfig={() => customersApiKeyStore.getCustomers()}
                    optionsValueKey="customerID"
                    optionsLabelKey="customerName"
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xl={12} lg={12} sm={12} xs={12}>
              <MoveContactComponent customerID={selectedCustomerID} handleNewMappedData={handleMappedData} />
            </Grid>
            <Grid item xl={12} lg={12} sm={12} xs={12}>
              <ContactMapListComponent
                contactID={contactID}
                customerID={selectedCustomerID}
                contactMappedData={newMappedData}
                isLoading={isLoading}
                isFetching={isFetching}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <div style={{ padding: '5px', paddingLeft: '16px', paddingRight: '16px' }}>
          <DialogActions className="spaceBetween">
            <Button variant="outlined" style={{ width: '130px' }} onClick={onClosed}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={(!customerChange && newMappedData?.length === 0) || isReadOnlyPermission}
              style={{ width: '130px' }}
            >
              Save
            </Button>
          </DialogActions>
        </div>
      </ReactHookForm>
    </Dialog>
  );
};
